import { isWindowDefined } from "./isWindowDefined";

/**
 * Hostnames that are considered local development environments where a
 * cookie/connection may legitimately be served over plain HTTP.
 */
const LOCALHOST_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

/**
 * Returns true only when the current page is served from a local development
 * host over **plain HTTP** (localhost / loopback / *.localhost, `http:`).
 *
 * This is intentionally *not* a general "is the hostname localhost?" check: an
 * `https://localhost` secure context returns `false` because it can and should
 * still receive `Secure` cookies. The helper exists solely to decide whether
 * the `Secure` cookie flag can be safely omitted — it must be omitted on
 * plain-HTTP localhost dev (the browser drops `Secure` cookies set over HTTP),
 * but MUST always be set everywhere else so an auth/JWT cookie can never travel
 * over plain HTTP (SMA-1711).
 *
 * Falls back to `false` (i.e. treat as secure, so `Secure` will be applied)
 * when there is no window — the safe default for SSR / non-browser contexts.
 */
export function isInsecureLocalhost(): boolean {
  if (!isWindowDefined()) {
    return false;
  }

  const { hostname, protocol } = window.location;

  // A secure-context localhost (e.g. https://localhost) can and should still
  // use Secure; only treat plain-HTTP localhost as the dev exception.
  if (protocol === "https:") {
    return false;
  }

  return LOCALHOST_HOSTNAMES.has(hostname) || hostname.endsWith(".localhost");
}
